import { Injectable } from "@angular/core";

import { Announcement, SeenAnnouncements } from "../models/Announcement";
import { Company } from "../models/Company";
import { Language, LocalizationInStorage } from "../models/Language";
import { Tool } from "../models/Tool";
import { ToolColumn } from "../models/ToolColumn";
import { ToolCounter } from "../models/ToolCounter";
import { UserProfile } from "../models/UserProfile";
import { isIDeserializable } from "../utils/type.utils";
import { Basket, ToolCounterType, ViewType } from "./../models/Enums";
import { ToolFilter } from "./../models/ToolFilter";

export const RETURN_URL = "returnUrl";

const USER_PROFILE: string = "user";
const ACCESS_TOKEN_EXPIRATION_IN_MSEC = 1000 * 60 * 60; // 1 hour
const CURRENT_REGION = "regions.current";
const TOOLS_DROP_COLUMN_STATE = "toolsDropColumnState";
const TOOL_FILTER_STATE = ".toolsFilterState";
const LANGUAGE_KEY: string = "translate.language";
const LANGUAGE_LIST = "translate.language_list";
const LOCALIZATION_PREFIX: string = "localization.";
const LANGUAGE_LIST_EXPIRATION_IN_MSEC = 1000 * 60 * 60; // 1 hour
const LOGIN_PROGRESS_EXPIRATION_IN_MSEC = 1000 * 20; // 20 sec
const IS_LOGIN_IN_PROGRESS = "isLoginInProgress";
const SEEN_ANNOUNCEMENTS = "seen_announcements";
const ANNOUNCEMENT_LAST_CHECKED = "announcement_last_checked";
const LAST_ANNOUNCEMENT = "last_announcement";

class CacheItem<T> {
  value: T;
  expiry: number;

  constructor(item: T, expiry: number) {
    this.value = item;
    this.expiry = expiry;
  }
}

@Injectable({
  providedIn: "root",
})
export class StorageService {
  constructor() {}

  cleanCompanyStorage() {
    localStorage.removeItem("company");

    //* Remove also tool counters as they are fetched per company
    localStorage.removeItem("toolCounters");
  }

  public clearUserProfileStorage(): void {
    localStorage.removeItem(USER_PROFILE);
  }

  public getSeenAnnouncements(): SeenAnnouncements {
    const seenAnnouncementsStr = localStorage.getItem(SEEN_ANNOUNCEMENTS);
    if (!seenAnnouncementsStr) {
      return { announcementIds: [] } as SeenAnnouncements;
    }

    return JSON.parse(seenAnnouncementsStr);
  }

  public getLastAnnouncement(): Announcement | null {
    return this.getWithExpiry(LAST_ANNOUNCEMENT, Announcement);
  }

  public setLastAnnouncement(announcement: Announcement): void {
    const ttl = new Date(announcement.validUntil).getTime() - new Date().getTime();
    this.setWithExpiry(LAST_ANNOUNCEMENT, announcement, ttl);
  }

  public getLastAnnouncementCheckDate(): Date | null {
    const checkedTime = localStorage.getItem(ANNOUNCEMENT_LAST_CHECKED);
    return checkedTime ? new Date(checkedTime) : null;
  }

  public clearLastAnnouncementCheckDate(): void {
    localStorage.removeItem(ANNOUNCEMENT_LAST_CHECKED);
  }

  public setLastAnnouncementCheckDate(date: Date): void {
    localStorage.setItem(ANNOUNCEMENT_LAST_CHECKED, date.toISOString());
  }

  public markAnnouncementAsSeen(announcement: Announcement): void {
    const announcements = this.getSeenAnnouncements();
    announcements.announcementIds.push(announcement.announcementId);
    localStorage.setItem(SEEN_ANNOUNCEMENTS, JSON.stringify(announcements));
  }

  get company(): Company | null {
    const company = localStorage.getItem("company");
    if (!company) return null;
    return new Company().deserialize(JSON.parse(company));
  }

  setCompany(company: Company) {
    localStorage.setItem("company", JSON.stringify(company));
  }

  public getRegion(): string | null {
    return localStorage.getItem(CURRENT_REGION);
  }

  public setRegion(region: string): void {
    localStorage.setItem(CURRENT_REGION, region);
  }

  setDepartment(department) {
    localStorage.setItem("department", department);
  }

  setDepartments(departments) {
    localStorage.setItem("departments", departments);
  }

  getCompanies() {
    return this.getUser()?.companies;
  }

  getFamCodes() {
    return localStorage.getItem("famCodes");
  }

  getDepartments() {
    return localStorage.getItem("departments");
  }

  getFilterState(viewType: ViewType): ToolFilter {
    const filterState =
      <ToolFilter>JSON.parse(localStorage.getItem(viewType + ".toolsFilterState")) ??
      new ToolFilter();
    filterState.companyId = this.company.name;
    filterState.department = this.company.department?.name;

    return filterState;
  }

  setFilterState(filter: ToolFilter, viewType: ViewType) {
    localStorage.setItem(viewType + ".toolsFilterState", JSON.stringify(filter));
  }

  setGridState(gridState: { pageSize: number }) {
    localStorage.setItem("toolsGridState", JSON.stringify(gridState));
  }

  restoreGridState() {
    return JSON.parse(localStorage.getItem("toolsGridState"));
  }

  public getUser(): UserProfile | null {
    const user = this.getWithExpiry(USER_PROFILE, UserProfile);
    return user;
  }

  public setUser(userProfile: UserProfile): void {
    return this.setWithExpiry(USER_PROFILE, userProfile, ACCESS_TOKEN_EXPIRATION_IN_MSEC);
  }

  setCounters(name: ToolCounterType, value: number | null) {
    const counterState =
      <ToolCounter>JSON.parse(localStorage.getItem("toolCounters")) ?? new ToolCounter();
    counterState.company = this.company;

    if (name === ToolCounterType.Service) counterState.toolsDueServiceCount = value;
    else if (name === ToolCounterType.Calibration) counterState.toolsDueCalibrationCount = value;
    else if (name === ToolCounterType.AwaitingPO) counterState.toolsAwatingPoCount = value;

    localStorage.setItem("toolCounters", JSON.stringify(counterState));
  }

  getCounter(name: ToolCounterType) {
    const counterState =
      <ToolCounter>JSON.parse(localStorage.getItem("toolCounters")) ??
      new ToolCounter(this.company);

    if (
      !(
        counterState.company.name == this.company.name &&
        counterState.company?.department?.name == this.company?.department?.name
      )
    ) {
      localStorage.removeItem("toolCounters");
      return null;
    }

    if (name === ToolCounterType.Service) return counterState.toolsDueServiceCount;
    else if (name === ToolCounterType.Calibration) return counterState.toolsDueCalibrationCount;
    else if (name === ToolCounterType.AwaitingPO) return counterState.toolsAwatingPoCount;

    return null;
  }

  public getToolColumns(): ToolColumn[] | null {
    return JSON.parse(localStorage.getItem(TOOLS_DROP_COLUMN_STATE));
  }

  public setToolColumns(columns: ToolColumn[]): void {
    localStorage.setItem(TOOLS_DROP_COLUMN_STATE, JSON.stringify(columns));
  }

  public getToolFilter(viewType: ViewType): ToolFilter | null {
    return JSON.parse(localStorage.getItem(`${viewType}${TOOL_FILTER_STATE}`));
  }

  public setToolFilter(viewType: ViewType, toolFilter: ToolFilter): void {
    localStorage.setItem(`${viewType}${TOOL_FILTER_STATE}`, JSON.stringify(toolFilter));
  }

  public getLanguage(): Language | null {
    return JSON.parse(localStorage.getItem(LANGUAGE_KEY));
  }

  public setLanguage(language: Language): void {
    localStorage.setItem(LANGUAGE_KEY, JSON.stringify(language));
  }

  public getLanguageList(): Language[] {
    const languages = this.getWithExpiry<Language[]>(LANGUAGE_LIST);
    return languages ?? [];
  }

  public setLanguageList(languages: Language[]): void {
    this.setWithExpiry(LANGUAGE_LIST, languages, LANGUAGE_LIST_EXPIRATION_IN_MSEC);
  }

  public clearLanguageList(): void {
    localStorage.removeItem(LANGUAGE_LIST);
  }

  public getLocalization(languageCode: string): LocalizationInStorage | null {
    return JSON.parse(localStorage.getItem(`${LOCALIZATION_PREFIX}${languageCode}`));
  }

  public setLocalization(languageCode: string, localization: LocalizationInStorage): void {
    return localStorage.setItem(
      `${LOCALIZATION_PREFIX}${languageCode}`,
      JSON.stringify(localization),
    );
  }

  public clearLocalization(languageCode: string): void {
    localStorage.removeItem(`${LOCALIZATION_PREFIX}${languageCode}`);
  }

  public getBasket(basket: Basket): Tool[] | null {
    return JSON.parse(localStorage.getItem(basket));
  }

  public setBasket(basket: Basket, tools: Tool[]): void {
    return localStorage.setItem(basket, JSON.stringify(tools));
  }

  public clearBasket(basket: Basket): void {
    localStorage.removeItem(basket);
  }

  public getLoginInProgressState(): boolean {
    return Boolean(this.getWithExpiry(IS_LOGIN_IN_PROGRESS));
  }

  public clearLoginInProgressState(): void {
    localStorage.removeItem(IS_LOGIN_IN_PROGRESS);
  }

  public setIsLoginInProgressState(): void {
    this.setWithExpiry(IS_LOGIN_IN_PROGRESS, "true", LOGIN_PROGRESS_EXPIRATION_IN_MSEC);
  }

  public getReturnUrl(): string | null {
    return localStorage.getItem(RETURN_URL);
  }

  public setReturnUrl(url: string): void {
    localStorage.setItem(RETURN_URL, url);
  }

  public clearReturnUrl(): void {
    localStorage.removeItem(RETURN_URL);
  }

  private setWithExpiry<T>(key: string, value: T, ttl: number): void {
    const expiry = new Date().getTime() + ttl;

    const item = new CacheItem(value, expiry);
    localStorage.setItem(key, JSON.stringify(item));
  }

  /**
   * Gets the item from the local storage.
   * Returns the item if the item is not expired, otherwise null.
   * Applies deserialization logic if the type implements `IDeserialize` interface.
   * To be able to check if the item implements `IDeserialize` logic, `type` argument
   * is provided which can be any type that has constructor. Default value for `type`
   * argument is null.
   */
  private getWithExpiry<T extends object>(key: string, type: new () => T = null): T | null {
    const itemStr = localStorage.getItem(key);

    if (!itemStr) {
      return null;
    }

    const item = JSON.parse(itemStr) as CacheItem<T>;
    const now = new Date();
    // check the expiration
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    // create the instance of the type if we have one
    const instance = type ? new type() : null;

    if (isIDeserializable<T>(instance)) {
      return instance.deserialize(item.value);
    }

    return item.value;
  }
}
